import { useCallback, useState } from 'react';
import type { MouseEvent } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';

import type { Id } from '@e-socna/data-layer';

import { environment } from '../../../../environment';
import { useIsInViewport } from '../../../../hooks/useIsInViewport.hook';

import { EllipsisTooltip } from '../../../EllipsisTooltip';
import { FileTypeIcon } from '../FileTypeIcon';
import { downloadFile } from '../FilesConsultation/utils/download-file.util';
import { formatFileSize } from '../FilesConsultation/utils/format-file-size.util';
import type { FileItem } from '../FilesConsultation/types/file-item.type';

interface Props {
  readonly canMove: boolean;
  readonly file: FileItem;
  readonly isSelected: boolean;
  readonly onDelete: (file: FileItem) => void;
  readonly onMove: (file: FileItem) => void;
  readonly onRename: (file: FileItem) => void;
  readonly onToggleSelect: (fileId: Id) => void;
  readonly onView: (file: FileItem) => void;
}

export const FileCard = ({
  canMove,
  file,
  isSelected,
  onDelete,
  onMove,
  onRename,
  onToggleSelect,
  onView,
}: Readonly<Props>) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [isThumbnailLoaded, setIsThumbnailLoaded] = useState(false);
  const [hasThumbnailFailed, setHasThumbnailFailed] = useState(false);
  const { elementRef, isInViewport } = useIsInViewport<HTMLDivElement>();

  const handleThumbnailLoad = useCallback(() => {
    setIsThumbnailLoaded(true);
  }, []);

  const handleThumbnailError = useCallback(() => {
    setHasThumbnailFailed(true);
  }, []);

  const openMenu = useCallback((event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  }, []);

  const closeMenu = useCallback(() => {
    setMenuAnchor(null);
  }, []);

  const handleToggleSelect = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      onToggleSelect(file._id);
    },
    [file, onToggleSelect],
  );

  const handleView = useCallback(() => {
    closeMenu();
    onView(file);
  }, [closeMenu, file, onView]);

  const handleDownload = useCallback(() => {
    closeMenu();
    downloadFile(file.uri);
  }, [closeMenu, file.uri]);

  const handleRename = useCallback(() => {
    closeMenu();
    onRename(file);
  }, [closeMenu, file, onRename]);

  const handleMove = useCallback(() => {
    closeMenu();
    onMove(file);
  }, [closeMenu, file, onMove]);

  const handleDelete = useCallback(() => {
    closeMenu();
    onDelete(file);
  }, [closeMenu, file, onDelete]);

  return (
    <Card
      sx={{
        border: 2,
        borderColor: isSelected ? 'primary.main' : 'transparent',
        position: 'relative',
      }}
      variant="outlined"
    >
      <Checkbox
        checked={isSelected}
        onClick={handleToggleSelect}
        sx={{
          bgcolor: 'background.paper',
          borderRadius: '50%',
          left: 4,
          position: 'absolute',
          top: 4,
          zIndex: 1,
        }}
      />
      <IconButton
        onClick={openMenu}
        sx={{
          bgcolor: 'background.paper',
          position: 'absolute',
          right: 4,
          top: 4,
          zIndex: 1,
        }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={menuAnchor} onClose={closeMenu} open={!!menuAnchor}>
        <MenuItem onClick={handleView}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Consulter</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDownload}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Télécharger</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleRename}>
          <ListItemIcon>
            <DriveFileRenameOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Renommer</ListItemText>
        </MenuItem>
        <MenuItem disabled={!canMove} onClick={handleMove}>
          <ListItemIcon>
            <DriveFileMoveIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Changer de catégorie</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <ListItemIcon>
            <DeleteIcon color="error" fontSize="small" />
          </ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Supprimer</ListItemText>
        </MenuItem>
      </Menu>
      <CardActionArea onClick={handleView}>
        <Box ref={elementRef} sx={{ aspectRatio: '1 / 1', width: '100%' }}>
          {(!file.thumbnailVariantId ||
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion
            !!hasThumbnailFailed ||
            !isThumbnailLoaded) && <FileTypeIcon mimeType={file.mimeType} />}
          {!!(
            file.thumbnailVariantId &&
            !hasThumbnailFailed &&
            isInViewport
          ) && (
            <Box
              alt={file.originalName}
              component="img"
              onError={handleThumbnailError}
              onLoad={handleThumbnailLoad}
              src={`${environment.backendUrl}/${file.uri}?thumbnail=true`}
              sx={{
                display: isThumbnailLoaded ? 'block' : 'none',
                height: '100%',
                objectFit: 'cover',
                width: '100%',
              }}
            />
          )}
        </Box>
        <Box sx={{ p: 1 }}>
          <EllipsisTooltip title={file.originalName}>
            <Typography
              sx={{
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
                display: '-webkit-box',
                overflow: 'hidden',
                wordBreak: 'break-word',
              }}
              variant="body2"
            >
              {file.originalName}
            </Typography>
          </EllipsisTooltip>
          <Typography color="text.secondary" variant="caption">
            {formatFileSize(file.size)}
          </Typography>
        </Box>
      </CardActionArea>
    </Card>
  );
};
